import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { getIpdLabTestStatus } from "../labTestWorkflowUtils";
import IpdWorkflowCollectModal from "./IpdWorkflowCollectModal";
import IpdWorkflowResultsModal from "./IpdWorkflowResultsModal";
import {
  QUEUE_ACTION_BTN_SX,
  QUEUE_CARD_TEST_LIST_HEIGHT,
  QUEUE_FIELD_ICON_SLOT_SX,
  QUEUE_FIELD_LABEL_SX,
  QUEUE_FIELD_VALUE_SX,
  QUEUE_FIELDS_ROW_SX,
  queueCardPaperSx,
  formatQueueDateTimeLabels,
  displayQueueId,
} from "./workflowCardLayout";
import BookedByPatientMark from "./BookedByPatientMark";

const statusColor = (status) => {
  if (status === "COMPLETED" || status === "COMPLETE") return "success";
  if (status === "COLLECTED") return "info";
  if (status === "PENDING") return "warning";
  if (status === "REJECTED") return "error";
  return "default";
};

const QueueField = ({ icon, label, value, bold = true }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
    {icon}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={QUEUE_FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 600 : 400}
        color="text.primary"
        sx={QUEUE_FIELD_VALUE_SX}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

const IpdPatientWorkflowCard = ({
  group,
  onRefresh,
  patientTypeLabel,
  isTestBookedByPatient = () => false,
}) => {
  const [openCollectModal, setOpenCollectModal] = useState(false);
  const [openResultModal, setOpenResultModal] = useState(false);

  const [collectProcessing, setCollectProcessing] = useState(false);
  const [resultSubmitting, setResultSubmitting] = useState(false);

  const labTests = group?.labTests || [];
  const hasPending = labTests.some((test) => getIpdLabTestStatus(test) === "PENDING");
  const hasCollected = labTests.some((test) => getIpdLabTestStatus(test) === "COLLECTED");

  const collectDisabled = !hasPending || collectProcessing || resultSubmitting;
  const addResultDisabled = !hasCollected || collectProcessing || resultSubmitting;

  const { dateLabel, timeLabel } = formatQueueDateTimeLabels(
    group?.appointmentDateTime,
    group?.createdAt,
    group?.testDate,
    labTests
  );
  const appointmentIdLabel = displayQueueId(group?.appointmentId ?? group?.appointment_id);
  const linkedIpdTest = labTests.find(
    (test) => test?.prescriptionId || test?.ipdPrescriptionId
  );
  const prescriptionIdLabel = displayQueueId(
    group?.prescriptionId ??
      group?.prescription_id ??
      group?.ipdPrescriptionId ??
      linkedIpdTest?.prescriptionId ??
      linkedIpdTest?.ipdPrescriptionId
  );

  const handleRefresh = async () => {
    if (onRefresh) await onRefresh();
  };

  return (
    <>
      <Paper elevation={3} sx={queueCardPaperSx}>
        <Stack spacing={0.5} sx={{ height: "100%" }}>
          <Box sx={QUEUE_FIELDS_ROW_SX}>
            <QueueField
              icon={
                <ConfirmationNumberIcon
                  fontSize="small"
                  color="action"
                  sx={{ fontSize: "0.7rem" }}
                />
              }
              label="Appt ID"
              value={appointmentIdLabel}
              bold={false}
            />
            <QueueField
              icon={
                <AssignmentIcon
                  fontSize="small"
                  color="action"
                  sx={{ fontSize: "0.7rem" }}
                />
              }
              label="Pres ID"
              value={prescriptionIdLabel}
              bold={false}
            />
          </Box>
          <Divider sx={{ my: 0.25 }} />
          <Box sx={QUEUE_FIELDS_ROW_SX}>
            <QueueField
              icon={
                <PersonIcon
                  fontSize="small"
                  color="primary"
                  sx={{ fontSize: "0.7rem" }}
                />
              }
              label={patientTypeLabel ? `Patient - ${patientTypeLabel}` : "Patient"}
              value={group.patientName}
            />
            <QueueField
              icon={
                <LocalHospitalIcon
                  fontSize="small"
                  color="primary"
                  sx={{ fontSize: "0.7rem" }}
                />
              }
              label="Doctor"
              value={group.doctorName || group.assignedByName}
            />
          </Box>

          <Box sx={QUEUE_FIELDS_ROW_SX}>
            <QueueField
              icon={
                <CalendarTodayIcon
                  fontSize="small"
                  color="action"
                  sx={{ fontSize: "0.7rem" }}
                />
              }
              label="Date"
              value={dateLabel}
              bold={false}
            />
            <QueueField
              icon={
                <AccessTimeIcon
                  fontSize="small"
                  color="action"
                  sx={{ fontSize: "0.7rem" }}
                />
              }
              label="Time"
              value={timeLabel}
              bold={false}
            />
          </Box>

          <Divider sx={{ my: 0.25 }} />

          <Box
            sx={{
              ...QUEUE_FIELDS_ROW_SX,
              height: QUEUE_CARD_TEST_LIST_HEIGHT,
              maxHeight: QUEUE_CARD_TEST_LIST_HEIGHT,
              overflowY: "auto",
              overflowX: "hidden",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.25, flex: 1, minWidth: 0 }}>
              <Box sx={QUEUE_FIELD_ICON_SLOT_SX} aria-hidden />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ...QUEUE_FIELD_LABEL_SX, fontWeight: 600 }}
                >
                  Test
                </Typography>
                {labTests.map((test) => {
                  const testName = test.resolvedTestName || test.testName;
                  return (
                    <Typography
                      key={test.id}
                      variant="body2"
                      color="text.primary"
                      title={testName}
                      sx={{ ...QUEUE_FIELD_VALUE_SX, fontWeight: 400, minHeight: 16 }}
                    >
                      {testName || "—"}
                      {(() => {
                        const booked = isTestBookedByPatient(test);
                        return booked ? <BookedByPatientMark detail={booked.whenLabel} /> : null;
                      })()}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.25, flex: 1, minWidth: 0 }}>
              <Box sx={QUEUE_FIELD_ICON_SLOT_SX} aria-hidden />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ...QUEUE_FIELD_LABEL_SX, fontWeight: 600 }}
                >
                  Status
                </Typography>
                {labTests.map((test) => {
                  const status = getIpdLabTestStatus(test);
                  return (
                    <Box
                      key={`status-${test.id}`}
                      sx={{ minHeight: 16, display: "flex", alignItems: "center" }}
                    >
                      <Chip
                        size="small"
                        label={status === "COMPLETED" ? "COMPLETE" : status}
                        color={statusColor(status)}
                        sx={{
                          height: 16,
                          fontSize: "0.55rem",
                          "& .MuiChip-label": { px: 0.45, py: 0 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, pt: 0.25, mt: "auto" }}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => setOpenCollectModal(true)}
              disabled={collectDisabled}
              sx={QUEUE_ACTION_BTN_SX}
            >
              {collectProcessing ? "Processing..." : "Collect"}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setOpenResultModal(true)}
              disabled={addResultDisabled}
              sx={QUEUE_ACTION_BTN_SX}
            >
              {resultSubmitting ? "Processing..." : "Add Result"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <IpdWorkflowCollectModal
        open={openCollectModal}
        onClose={() => {
          setOpenCollectModal(false);
          setCollectProcessing(false);
        }}
        group={group}
        onSampleCollected={handleRefresh}
        onProcessingChange={setCollectProcessing}
      />

      <IpdWorkflowResultsModal
        open={openResultModal}
        onClose={() => {
          setOpenResultModal(false);
          setResultSubmitting(false);
        }}
        group={group}
        onSubmitted={handleRefresh}
        onSubmittingChange={setResultSubmitting}
      />
    </>
  );
};

export default IpdPatientWorkflowCard;
